import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  PlusCircle, Search, LayoutGrid, List, Phone, Mail, 
  DollarSign, Calendar, FileText, MessageSquare, CheckSquare,
  Clock, User, Building, Truck, ChevronRight, ExternalLink, Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import CRMLayout from '../../components/CRM/CRMLayout';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Full 14-stage workflow mapped to Kanban columns
const PIPELINE_STAGES = [
  { id: 'lead', label: 'Lead', color: 'bg-blue-500', textColor: 'text-blue-400', hex: '#3B82F6' },
  { id: 'discovery', label: 'Discovery', color: 'bg-amber-500', textColor: 'text-amber-400', hex: '#F59E0B' },
  { id: 'pre_assessment', label: 'Pre-Assessment', color: 'bg-orange-500', textColor: 'text-orange-400', hex: '#F97316' },
  { id: 'docs_requested', label: 'Docs Requested', color: 'bg-yellow-500', textColor: 'text-yellow-400', hex: '#EAB308' },
  { id: 'application_ready', label: 'App Ready', color: 'bg-lime-500', textColor: 'text-lime-400', hex: '#84CC16' },
  { id: 'submitted_to_lender', label: 'Submitted', color: 'bg-purple-500', textColor: 'text-purple-400', hex: '#8B5CF6' },
  { id: 'conditional_approval', label: 'Cond. Approved', color: 'bg-green-500', textColor: 'text-green-400', hex: '#22C55E' },
  { id: 'conditions_outstanding', label: 'Conditions', color: 'bg-cyan-500', textColor: 'text-cyan-400', hex: '#06B6D4' },
  { id: 'final_approval', label: 'Final Approval', color: 'bg-emerald-500', textColor: 'text-emerald-400', hex: '#10B981' },
  { id: 'settlement_booked', label: 'Settlement Booked', color: 'bg-teal-500', textColor: 'text-teal-400', hex: '#14B8A6' },
  { id: 'settled', label: 'Settled', color: 'bg-green-600', textColor: 'text-green-300', hex: '#059669' },
];

const INACTIVE_STAGES = [
  { id: 'post_settlement', label: 'Post-Settlement', color: 'bg-green-800' },
  { id: 'declined', label: 'Declined', color: 'bg-red-500' },
  { id: 'lost', label: 'Lost', color: 'bg-gray-500' },
];

// Map legacy stage IDs to new ones for display
const LEGACY_MAP = {
  contacted: 'discovery',
  application: 'docs_requested',
  submitted: 'submitted_to_lender',
  approved: 'conditional_approval',
  docs_out: 'conditions_outstanding',
};

function mapStage(stage) {
  return LEGACY_MAP[stage] || stage;
}

function findStageConfig(stageId) {
  const mapped = mapStage(stageId);
  return PIPELINE_STAGES.find(s => s.id === mapped) || INACTIVE_STAGES.find(s => s.id === mapped) || { id: mapped, label: mapped, color: 'bg-gray-500', textColor: 'text-gray-400' };
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)}w ago`;
  return `${Math.floor(diff / 2592000)}mo ago`;
}

export default function Pipeline() {
  const [searchParams] = useSearchParams();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('kanban');
  const [draggedDeal, setDraggedDeal] = useState(null);

  useEffect(() => { fetchDeals(); }, []);

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API}/crm/deals`);
      setDeals(response.data);
    } catch (error) {
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStageId) => {
    e.preventDefault();
    if (!draggedDeal) { setDraggedDeal(null); return; }
    const currentMapped = mapStage(draggedDeal.stage);
    if (currentMapped === targetStageId) { setDraggedDeal(null); return; }
    try {
      await axios.post(`${API}/crm/workflow/transition`, {
        deal_id: draggedDeal.id,
        target_stage: targetStageId,
        force: true
      });
      setDeals(prev => prev.map(d =>
        d.id === draggedDeal.id ? { ...d, stage: targetStageId } : d
      ));
      const stageLabel = PIPELINE_STAGES.find(s => s.id === targetStageId)?.label || targetStageId;
      toast.success(`Moved to ${stageLabel}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    }
    setDraggedDeal(null);
  };

  const filteredDeals = deals.filter(deal => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      deal.client_name?.toLowerCase().includes(s) ||
      deal.business_name?.toLowerCase().includes(s) ||
      deal.abn?.includes(search) ||
      deal.client_email?.toLowerCase().includes(s)
    );
  });

  const getDealsByStage = (stageId) => filteredDeals.filter(d => mapStage(d.stage) === stageId);
  const getStageValue = (stageId) => getDealsByStage(stageId).reduce((s, d) => s + (d.amount || 0), 0);

  const totalPipelineValue = filteredDeals
    .filter(d => !['settled', 'post_settlement', 'declined', 'lost'].includes(mapStage(d.stage)))
    .reduce((s, d) => s + (d.amount || 0), 0);

  if (loading) {
    return (
      <CRMLayout>
        <div className="min-h-screen bg-navy-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
        </div>
      </CRMLayout>
    );
  }

  return (
    <CRMLayout>
    <div className="min-h-screen bg-navy-900">
      {/* Header */}
      <div className="bg-navy-800 border-b border-white/5 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading text-white uppercase" data-testid="pipeline-title">Deal Pipeline</h1>
              <p className="text-gray-400 text-sm">
                {filteredDeals.length} deals &middot; ${totalPipelineValue.toLocaleString('en-AU')} pipeline value
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search deals..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="pipeline-search"
                  className="pl-10 pr-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 w-64"
                />
              </div>
              <div className="flex border border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('kanban')}
                  data-testid="view-kanban-btn"
                  className={`p-2 ${viewMode === 'kanban' ? 'bg-purple-600 text-white' : 'bg-navy-800 text-gray-400'}`}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  data-testid="view-list-btn"
                  className={`p-2 ${viewMode === 'list' ? 'bg-purple-600 text-white' : 'bg-navy-800 text-gray-400'}`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
              <Link to="/crm/deals/new">
                <Button className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="new-deal-pipeline-btn">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  New Deal
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      {viewMode === 'kanban' ? (
        <div className="overflow-x-auto">
          <div className="flex gap-2 p-4 min-w-max">
            {PIPELINE_STAGES.map((stage) => {
              const stageDeals = getDealsByStage(stage.id);
              const stageVal = getStageValue(stage.id);
              return (
                <div
                  key={stage.id}
                  className="w-[260px] flex-shrink-0"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage.id)}
                  data-testid={`stage-column-${stage.id}`}
                >
                  {/* Column Header */}
                  <div className={`rounded-t-lg px-3 py-2.5 ${stage.color}`}>
                    <div className="flex items-center justify-between text-white">
                      <span className="font-semibold text-xs">{stage.label}</span>
                      <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-[10px] font-bold">
                        {stageDeals.length}
                      </span>
                    </div>
                    {stageVal > 0 && (
                      <p className="text-white/70 text-[10px] mt-0.5">
                        ${stageVal.toLocaleString('en-AU')}
                      </p>
                    )}
                  </div>
                  {/* Cards */}
                  <div className="bg-navy-800/30 rounded-b-lg p-1.5 min-h-[450px] space-y-1.5">
                    {stageDeals.map((deal) => (
                      <DealCard
                        key={deal.id}
                        deal={deal}
                        stage={stage}
                        onDragStart={handleDragStart}
                      />
                    ))}
                    {stageDeals.length === 0 && (
                      <div className="text-center py-10 text-gray-600 text-[10px]">
                        Drop deals here
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* List View */
        <div className="px-6 py-6">
          <div className="bg-navy-800 rounded-xl border border-white/5 overflow-hidden">
            <table className="w-full">
              <thead className="bg-navy-900">
                <tr className="text-left text-gray-400 text-xs uppercase tracking-wider">
                  <th className="p-4">Client</th>
                  <th className="p-4">Business</th>
                  <th className="p-4">Amount</th>
                  <th className="p-4">Type</th>
                  <th className="p-4">Asset</th>
                  <th className="p-4">Stage</th>
                  <th className="p-4">Updated</th>
                  <th className="p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDeals.map((deal) => {
                  const sc = findStageConfig(deal.stage);
                  return (
                    <tr key={deal.id} className="border-t border-white/5 hover:bg-navy-900/50 group">
                      <td className="p-4">
                        <Link to={`/crm/deals/${deal.id}/workspace`} className="text-white hover:text-purple-400 font-medium">
                          {deal.client_name}
                        </Link>
                        {deal.client_phone && (
                          <p className="text-gray-500 text-xs">{deal.client_phone}</p>
                        )}
                      </td>
                      <td className="p-4 text-gray-400 text-sm">{deal.business_name || '-'}</td>
                      <td className="p-4 text-white font-medium">${deal.amount?.toLocaleString('en-AU')}</td>
                      <td className="p-4 text-gray-400 text-sm capitalize">{deal.finance_type}</td>
                      <td className="p-4 text-gray-500 text-sm max-w-[150px] truncate">{deal.asset_description || '-'}</td>
                      <td className="p-4">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium text-white ${sc.color}`}>
                          {sc.label}
                        </span>
                      </td>
                      <td className="p-4 text-gray-500 text-xs">{timeAgo(deal.updated_at)}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {deal.client_phone && (
                            <a href={`tel:${deal.client_phone}`} className="p-1.5 text-gray-500 hover:text-green-400 rounded" title="Call">
                              <Phone className="h-3.5 w-3.5" />
                            </a>
                          )}
                          {deal.client_email && (
                            <a href={`mailto:${deal.client_email}`} className="p-1.5 text-gray-500 hover:text-blue-400 rounded" title="Email">
                              <Mail className="h-3.5 w-3.5" />
                            </a>
                          )}
                          <Link to={`/crm/deals/${deal.id}/workspace`} className="p-1.5 text-gray-500 hover:text-purple-400 rounded" title="View">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Inactive / Closed Deals */}
      <div className="px-6 py-4">
        <h3 className="text-gray-500 text-xs uppercase tracking-wider mb-3">Closed / Inactive</h3>
        <div className="flex gap-4 flex-wrap">
          {INACTIVE_STAGES.map((stage) => {
            const stageDeals = getDealsByStage(stage.id);
            return (
              <div key={stage.id} className="bg-navy-800 rounded-lg p-4 border border-white/5 min-w-[140px]" data-testid={`inactive-stage-${stage.id}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${stage.color}`}></div>
                  <span className="text-white text-sm">{stage.label}</span>
                  <span className="text-gray-400 text-sm">({stageDeals.length})</span>
                </div>
                {stageDeals.length > 0 && (
                  <p className="text-gray-500 text-xs mt-1 ml-5">
                    ${stageDeals.reduce((s, d) => s + (d.amount || 0), 0).toLocaleString('en-AU')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
    </CRMLayout>
  );
}

function DealCard({ deal, stage, onDragStart }) {
  const navigate = useNavigate();
  const docsReceived = deal.documents?.filter(d => d.is_received)?.length || 0;
  const docsTotal = deal.documents?.length || 0;
  const tasksOpen = deal.tasks?.filter(t => !t.is_completed)?.length || 0;
  const source = deal.source === 'finance_finder' ? 'Legacy AI Source' :
                 deal.source === 'referral' ? 'Referral' :
                 deal.source === 'manual' ? 'Manual' : deal.source || '';

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, deal)}
      className="bg-navy-900 rounded-lg border border-white/5 hover:border-white/10 cursor-grab active:cursor-grabbing transition-all hover:shadow-lg hover:shadow-purple-500/5 group"
      data-testid={`deal-card-${deal.id}`}
    >
      <div className="p-2.5 pb-2" onClick={() => navigate(`/crm/deals/${deal.id}/workspace`)}>
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-white font-semibold text-xs truncate flex-1 group-hover:text-purple-400 transition-colors cursor-pointer">
            {deal.client_name}
          </h3>
          <span className="text-white font-bold text-xs ml-2 flex-shrink-0">
            ${deal.amount?.toLocaleString('en-AU')}
          </span>
        </div>

        {deal.business_name && (
          <div className="flex items-center gap-1 text-gray-400 text-[10px] mb-1">
            <Building className="h-2.5 w-2.5 flex-shrink-0" />
            <span className="truncate">{deal.business_name}</span>
          </div>
        )}

        {deal.asset_description && (
          <div className="flex items-center gap-1 text-gray-500 text-[10px] mb-1">
            <Truck className="h-2.5 w-2.5 flex-shrink-0" />
            <span className="truncate">{deal.asset_description}</span>
          </div>
        )}

        {/* Source & Time */}
        <div className="flex items-center justify-between mt-1.5">
          {source && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
              deal.source === 'finance_finder' ? 'bg-purple-500/20 text-purple-400' :
              deal.source === 'referral' ? 'bg-blue-500/20 text-blue-400' :
              'bg-gray-500/20 text-gray-400'
            }`}>
              {source}
            </span>
          )}
          <span className="text-gray-600 text-[9px] flex items-center gap-0.5">
            <Clock className="h-2.5 w-2.5" />
            {timeAgo(deal.updated_at || deal.created_at)}
          </span>
        </div>
      </div>

      {/* Quick Action Bar */}
      <div className="border-t border-white/5 px-2 py-1 flex items-center justify-between">
        <div className="flex items-center gap-0.5">
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/crm/deals/${deal.id}/workspace`); }}
            className="p-1 text-gray-600 hover:text-yellow-400 rounded transition-colors relative"
            title={`${tasksOpen} open tasks`}
          >
            <CheckSquare className="h-3 w-3" />
            {tasksOpen > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-yellow-500 rounded-full text-[7px] text-white flex items-center justify-center font-bold">
                {tasksOpen}
              </span>
            )}
          </button>
          {deal.client_phone && (
            <a href={`tel:${deal.client_phone}`} onClick={(e) => e.stopPropagation()}
              className="p-1 text-gray-600 hover:text-green-400 rounded transition-colors" title="Call">
              <Phone className="h-3 w-3" />
            </a>
          )}
          {deal.client_email && (
            <a href={`mailto:${deal.client_email}`} onClick={(e) => e.stopPropagation()}
              className="p-1 text-gray-600 hover:text-blue-400 rounded transition-colors" title="Email">
              <Mail className="h-3 w-3" />
            </a>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/crm/deals/${deal.id}/workspace`); }}
            className="p-1 text-gray-600 hover:text-teal-400 rounded transition-colors relative"
            title={`${docsReceived}/${docsTotal} docs`}
          >
            <FileText className="h-3 w-3" />
            {docsTotal > 0 && (
              <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full text-[7px] text-white flex items-center justify-center font-bold ${
                docsReceived === docsTotal ? 'bg-green-500' : 'bg-gray-500'
              }`}>
                {docsReceived}
              </span>
            )}
          </button>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/crm/deals/${deal.id}/workspace`); }}
          className="p-1 text-gray-600 hover:text-purple-400 rounded transition-colors"
          title="View deal"
        >
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
